<template>
  <q-input
    :model-value="displayTextValue"
    @update:model-value="handleTextUpdate"
    :label="label"
    dense
    stack-label
    @keyup="$emit('keyup', fieldName)"
  >
    <template v-slot:prepend>
      <q-icon v-if="showIcon" :name="icon" color="primary" class="q-mr-xs" />
      <div
        v-if="swatchPosition === 'prepend'"
        :style="{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '1px solid #ccc',
          marginRight: '4px',
          background: displayColorValue
        }"
      ></div>
    </template>
    <template v-slot:append>
      <div
        v-if="swatchPosition === 'append'"
        :style="{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '1px solid #ccc',
          marginRight: '4px',
          background: displayColorValue
        }"
      ></div>
      <q-btn round dense flat icon="palette" size="xs">
        <q-popup-edit
          v-model="popupModel"
          class="windowed-popup"
          @before-show="handlePickerOpen"
          @hide="handlePopupHide"
        >
          <div>
            <q-color
              v-model="tempColorValue"
              format-model="hex"
              no-header
              no-footer
              default-view="spectrum"
              @update:model-value="handleColorDrag"
            />
            <div class="row justify-end q-mt-sm q-gutter-sm">
              <q-btn flat label="Cancel" color="primary" v-close-popup @click="handleColorCancel" />
              <q-btn flat label="Set" color="primary" v-close-popup @click="handleColorSave" />
            </div>
          </div>
        </q-popup-edit>
      </q-btn>
    </template>
  </q-input>
</template>

<script setup>
  import { ref, watch } from 'vue'
  import { useColorTranslator } from '@/composables/useColorTranslator'

  const props = defineProps({
    /** The human-readable text value (e.g., "Brown", "Blue") */
    textValue: {
      type: String,
      default: ''
    },
    /** The hex color value (e.g., "#6B4423") */
    colorValue: {
      type: String,
      default: '#000000'
    },
    /** The label for the input field */
    label: {
      type: String,
      required: true
    },
    /** The icon name to display */
    icon: {
      type: String,
      default: 'palette'
    },
    /** Show the prepend icon */
    showIcon: {
      type: Boolean,
      default: true
    },
    /** The type of color for translation: 'eyes', 'hair', or 'skin' */
    colorType: {
      type: String,
      required: true,
      validator: (value) => ['eyes', 'hair', 'skin'].includes(value)
    },
    /** Where to show the color swatch: 'append' (default) or 'prepend' */
    swatchPosition: {
      type: String,
      default: 'append',
      validator: (v) => ['append', 'prepend'].includes(v)
    },
    /** Field name for keyup event tracking */
    fieldName: {
      type: String,
      default: ''
    }
  })

  const emit = defineEmits(['update:textValue', 'update:colorValue', 'keyup', 'change'])

  const { hexToHumanReadable } = useColorTranslator()

  // Internal ref for color value to manage picker state
  const internalColorValue = ref(props.colorValue)

  // Temporary color value for the picker (only committed on save)
  const tempColorValue = ref(props.colorValue)

  // Display values (show preview while dragging, but don't commit)
  const displayTextValue = ref(
    props.textValue || hexToHumanReadable(props.colorValue, props.colorType)
  )
  const displayColorValue = ref(props.colorValue)

  // Popup model (needed for q-popup-edit to trigger save event)
  const popupModel = ref(props.textValue || hexToHumanReadable(props.colorValue, props.colorType))

  // Track if picker is open
  const isPickerOpen = ref(false)

  // Track whether user clicked Save or Cancel
  const shouldSave = ref(false)

  // Keep originals so we can revert on Cancel
  const originalTextValue = ref(props.textValue)
  const originalColorValue = ref(props.colorValue)

  // Watch for external prop changes
  watch(
    () => props.colorValue,
    (newVal) => {
      if (newVal !== internalColorValue.value) {
        internalColorValue.value = newVal
        tempColorValue.value = newVal
        if (!isPickerOpen.value) {
          displayColorValue.value = newVal
          // If text is empty, show human-readable fallback from hex
          if (!props.textValue || String(props.textValue).trim() === '') {
            const humanReadable = hexToHumanReadable(newVal, props.colorType)
            displayTextValue.value = humanReadable
            popupModel.value = humanReadable
          }
        }
      }
    }
  )

  watch(
    () => props.textValue,
    (newVal) => {
      if (!isPickerOpen.value) {
        if (newVal && String(newVal).trim() !== '') {
          displayTextValue.value = newVal
          popupModel.value = newVal
        } else {
          // When empty, show human-readable fallback from current hex
          const humanReadable = hexToHumanReadable(internalColorValue.value, props.colorType)
          displayTextValue.value = humanReadable
          popupModel.value = humanReadable
        }
      }
    }
  )

  // Sync if text is a hex code (user typed hex directly)
  const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/
  watch(
    () => props.textValue,
    (newVal) => {
      if (
        typeof newVal === 'string' &&
        HEX_RE.test(newVal) &&
        newVal !== internalColorValue.value
      ) {
        internalColorValue.value = newVal
        tempColorValue.value = newVal
        displayColorValue.value = newVal
        emit('update:colorValue', newVal)
      }
    }
  )

  /**
   * Handle text input changes (user typing in the field)
   */
  const handleTextUpdate = (value) => {
    displayTextValue.value = value
    emit('update:textValue', value)

    // If user types a hex code, update the color value too
    if (typeof value === 'string' && HEX_RE.test(value)) {
      internalColorValue.value = value
      tempColorValue.value = value
      displayColorValue.value = value
      emit('update:colorValue', value)
      emit('change', { text: value, color: value })
    }
  }

  /**
   * Handle picker opening
   */
  const handlePickerOpen = () => {
    isPickerOpen.value = true
    tempColorValue.value = internalColorValue.value
    // Default behavior: clicking outside should persist the selection
    // 'Cancel' button will flip this to false
    shouldSave.value = true
    // Keep popup text in sync with current display
    popupModel.value = displayTextValue.value
    // Capture originals to allow full revert on cancel
    originalTextValue.value = props.textValue
    originalColorValue.value = props.colorValue
  }

  /**
   * Handle color dragging (preview only, don't commit)
   */
  const handleColorDrag = () => {
    if (tempColorValue.value) {
      // Update display values to show preview
      displayColorValue.value = tempColorValue.value
      const humanReadable = hexToHumanReadable(tempColorValue.value, props.colorType)
      displayTextValue.value = humanReadable
      popupModel.value = humanReadable
      // Live preview upstream: update bound models during drag
      emit('update:textValue', humanReadable)
      emit('update:colorValue', tempColorValue.value)
    }
  }

  /**
   * Handle color picker save (user clicks Set button)
   */
  const handleColorSave = () => {
    shouldSave.value = true
  }

  /**
   * Handle color picker cancel (user clicks Cancel button)
   */
  const handleColorCancel = () => {
    shouldSave.value = false
    // Restore display values to committed values
    displayTextValue.value =
      props.textValue || hexToHumanReadable(props.colorValue, props.colorType)
    displayColorValue.value = props.colorValue
    tempColorValue.value = props.colorValue
    popupModel.value = displayTextValue.value
  }

  /**
   * Handle popup hide - commit changes if user clicked Save
   */
  const handlePopupHide = () => {
    isPickerOpen.value = false

    if (shouldSave.value && tempColorValue.value) {
      internalColorValue.value = tempColorValue.value
      const humanReadable = hexToHumanReadable(tempColorValue.value, props.colorType)

      // Update display values
      displayTextValue.value = humanReadable
      displayColorValue.value = tempColorValue.value
      popupModel.value = humanReadable

      // Emit both text and color updates
      emit('update:textValue', humanReadable)
      emit('update:colorValue', tempColorValue.value)
      emit('change', { text: humanReadable, color: tempColorValue.value })
    } else if (!shouldSave.value) {
      // User cancelled - restore original values
      const originalText = originalTextValue.value
      const originalColor = originalColorValue.value

      // Emit revert to parent to undo any live-drag updates
      emit('update:textValue', originalText)
      emit('update:colorValue', originalColor)
      emit('change', { text: originalText, color: originalColor })

      // Restore displays to originals (with human-readable fallback for empty text)
      displayTextValue.value =
        originalText && String(originalText).trim() !== ''
          ? originalText
          : hexToHumanReadable(originalColor, props.colorType)
      displayColorValue.value = originalColor
      tempColorValue.value = originalColor
      popupModel.value = displayTextValue.value
    }

    shouldSave.value = false
  }
</script>

<style scoped>
  /* Component-specific styles if needed */
</style>
